/**
 * The drivable car: its model, its motion, and the physics that drive it.
 *
 * **The model** is a small hatchback at real scale — 4.36 m long, 1.80 m wide,
 * 1.50 m tall, on 0.33 m wheels flush with the body — built the way the
 * houses are: flat-shaded facets, saturated paint, a cream roof, crisp dark
 * trim. The body is a side profile extruded across the car with chamfered
 * edges and the wheel arches cut out of it; the cabin is glass framed by
 * body-coloured pillars under a separate roof. Lamps glow, brake lights
 * brighten, and at night each headlight throws its own beam.
 *
 * **The motion** is what sells it as a machine rather than a sprite: wheels
 * that spin with the road speed and steer, a body on springs that squats
 * under power, dives under braking and leans out of a corner, and wheels that
 * follow the ground under them.
 *
 * **The physics** is arcade in scale but real in shape: grip-limited steering
 * (full lock at parking speed, a few degrees on the motorway — what the tyres
 * can actually hold), lateral grip that lets a handbraked rear step out into a
 * slide, gravity along the slope, an engine whose pull fades toward top speed,
 * and a parking brake that holds the car on a hill.
 */

import {
  BackSide,
  BoxGeometry,
  BufferAttribute,
  CanvasTexture,
  CircleGeometry,
  Color,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  LatheGeometry,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  TorusGeometry,
  MeshStandardMaterial,
  Shape,
  SpotLight,
  Vector2,
  Vector3,
} from 'three';
import type { BufferGeometry, Material, Object3D } from 'three';
import { createTruck } from './Truck.js';
import { createBus } from './Bus.js';
import { applyStreetLampLight } from './terrainMaterial.js';

export const VEHICLE = {
  length: 4.36,
  /** Track width, wheel centre to wheel centre. Used for the slope samples. */
  width: 1.57,
  /** Body width, mirrors aside. */
  bodyWidth: 1.8,
  wheelRadius: 0.33,
  tyreWidth: 0.23,
  /** Distance between front and rear axles, used for the turn model. */
  wheelbase: 2.68,
  /** Where the driver's eyes sit, for the first-person camera. */
  eyeHeight: 1.18,
  /** The driver's seat, from the car's centre: left-hand drive. */
  seatOffset: -0.37,
} as const;

export const DRIVE = {
  /** Engine pull from rest, m/s² — about 0–100 km/h in seven seconds. */
  power: 5.2,
  /** Service brakes, m/s²: a firm stop, just under 1 g. */
  brake: 9,
  reverseMax: 8,
  /** Top speed on a road, m/s (180 km/h). The engine's pull fades to it. */
  maxSpeed: 50,
  /** Air drag, per (m/s)². */
  drag: 0.00032,
  /** Rolling resistance, m/s². */
  rolling: 0.3,
  /** Engine braking when coasting, m/s². */
  engineBrake: 0.7,
  /** Steering lock at parking speed, radians. */
  steerLock: 0.6,
  /** Most sideways acceleration the tyres hold, m/s² — about 0.85 g. */
  cornerGrip: 8.3,
  /** How fast the wheel turns in, and how fast it returns, rad/s. */
  steerRate: 2.4,
  steerReturn: 4.2,
  /** How quickly sideways slip dies away, per second: tyres gripping. */
  lateralGrip: 9,
  /** ... and with the rear wheels locked by the handbrake. */
  handbrakeGrip: 0.9,
  handbrake: 6,
  /** Steepest slope the parking brake holds, as an acceleration, m/s². */
  parkingHold: 3,
  gravity: 9.81,
} as const;

/**
 * What a machine is, to the physics: its size and steering, its brakes and
 * grip, the air and the road against it, and its gearbox. A car's is
 * `CAR_CHASSIS`, the values every car has always had; a truck's are its own
 * (see `chassisOf`).
 */
export interface Chassis {
  wheelbase: number;
  /** Length and width overall, metres: for the ground under it, collisions and the camera. */
  length: number;
  width: number;
  steerLock: number;
  cornerGrip: number;
  steerRate: number;
  steerReturn: number;
  /** Service brakes, m/s². */
  brake: number;
  drag: number;
  rolling: number;
  reverseMax: number;
  /** Top speed, m/s: for a truck, its limiter. Undefined: the powertrain's own. */
  top?: number;
  /** Each gear's top speed, m/s, for an automated gearbox of its own. Undefined: the car's six. */
  gears?: readonly number[];
  /** A change up, s. */
  shift?: number;
  /** Engine and exhaust brake lifting off, m/s². Undefined: the powertrain's own. */
  coast?: number;
  /**
   * A truck's pull: watts of engine per kilogram, laden, which is what limits
   * it once rolling, and the most the driven wheels take off the line, m/s².
   * Undefined: the powertrain's pull through a car's gears.
   */
  power?: number;
  launch?: number;
}

export const CAR_CHASSIS: Chassis = {
  wheelbase: VEHICLE.wheelbase, length: VEHICLE.length, width: VEHICLE.bodyWidth,
  steerLock: DRIVE.steerLock, cornerGrip: DRIVE.cornerGrip, steerRate: DRIVE.steerRate, steerReturn: DRIVE.steerReturn,
  brake: DRIVE.brake, drag: DRIVE.drag, rolling: DRIVE.rolling, reverseMax: DRIVE.reverseMax,
};

/**
 * A rigid box truck, about twelve tonnes laden, and an articulated one, a
 * tractor and a loaded 13.6 m trailer at near forty: Swedish trucks'
 * limiters at 90 km/h, twelve gears changed by an automated box that takes
 * its time, brakes that need three or four times a car's distance to stop,
 * tyres that will not corner like a car's, a long wheelbase and so a wide
 * turning circle, and an exhaust brake when the pedal lifts.
 */
export const TRUCK_CHASSIS: Chassis = {
  wheelbase: 4.8, length: 8.6, width: 2.5,
  steerLock: 0.68, cornerGrip: 4.2, steerRate: 1.5, steerReturn: 2.6,
  brake: 6, drag: 0.00016, rolling: 0.09, reverseMax: 3.5,
  top: 25, gears: [2.4, 3.6, 5, 6.4, 8, 9.8, 11.8, 14, 16.5, 19, 22, 25], shift: 0.7, coast: 0.55,
  // About 250 kW at the wheels on fourteen tonnes laden.
  power: 17, launch: 1.6,
};
/**
 * A city bus: 12 m, 5.9 m between its axles, about 18 tonnes with people on.
 * Automatic, six speeds, quick off the line for its weight, limited to 80.
 */
export const BUS_CHASSIS: Chassis = {
  wheelbase: 5.9, length: 12, width: 2.55,
  steerLock: 0.72, cornerGrip: 4, steerRate: 1.5, steerReturn: 2.6,
  brake: 5.5, drag: 0.00018, rolling: 0.09, reverseMax: 3,
  top: 22, gears: [4, 7, 11, 15, 19, 22], shift: 0.4, coast: 0.5,
  power: 13, launch: 1.4,
};
/** An express bus: 15 m on three axles, a longer wheelbase, 100 km/h. */
export const COACH_CHASSIS: Chassis = {
  ...BUS_CHASSIS, wheelbase: 7.6, length: 14.8, steerLock: 0.66, top: 27.8, gears: [4, 7, 11, 15, 20, 24, 27.8], power: 12,
};
export const SEMI_CHASSIS: Chassis = {
  wheelbase: 3.7, length: 6.0, width: 2.5,
  steerLock: 0.7, cornerGrip: 3.6, steerRate: 1.4, steerReturn: 2.4,
  brake: 5, drag: 0.0001, rolling: 0.07, reverseMax: 3,
  top: 25, gears: [2.2, 3.3, 4.6, 6, 7.6, 9.4, 11.4, 13.6, 16, 18.8, 21.8, 25], shift: 0.8, coast: 0.45,
  // About 380 kW at the wheels on forty tonnes.
  power: 9.5, launch: 1.1,
};

/** The chassis of a kind of vehicle. */
export function chassisOf(kind: CarKind): Chassis {
  if (kind === 'busExpress') return COACH_CHASSIS;
  if (isBus(kind)) return BUS_CHASSIS;
  return kind === 'truck' ? TRUCK_CHASSIS : kind === 'semi' ? SEMI_CHASSIS : CAR_CHASSIS;
}

/** How far ahead the car looks for the ground falling away, seconds: near and far, for sharp crests and long ones. */
export const TAKEOFF_LOOKAHEAD: readonly number[] = [0.12, 0.25];
/**
 * How far the ground ahead must lie under the car's flight path to leave it,
 * metres. Physically any at all; a few centimetres keeps the terrain's
 * facets from flicking it into the air and straight back.
 */
const TAKEOFF_MARGIN = 0.06;
/**
 * The same for a car on its springs, metres: the wheels follow the ground
 * down as far as the suspension extends, about a quarter of a metre, so a
 * small hump, the dip after a kerb or the edge of a road surface keeps the
 * tyres on it. Only a real crest, falling away further than that faster than
 * gravity, lifts the car off.
 */
export const CAR_TAKEOFF_MARGIN = 0.25;

/**
 * Whether a car on the ground leaves it this step.
 *
 * Where would the car be in a moment if it were already flying — on the
 * vertical speed it actually has, falling under gravity — and is the ground
 * ahead clearly below that? At a cliff's edge a car still moving level sails
 * out over the drop; one already driving down a hill is moving down with it.
 * Only then do the tyres unload: over a crest taken fast, or off the end of
 * an embankment. A slow car, a climb, a kerb or a step onto a road all keep
 * the ground at or above the flight path, and the car on it. It settles half
 * a second after landing before it can fly again.
 */
export function leavesGround(
  speed: number, height: number,
  /** How fast the car is actually rising, m/s — its motion, not the ground's slope. */
  climb: number,
  /** The ground at each of `lookahead`'s times ahead. */
  groundAhead: ReadonlyArray<number | undefined>, sinceLanding: number,
  lookahead: readonly number[] = TAKEOFF_LOOKAHEAD,
  margin = TAKEOFF_MARGIN,
): boolean {
  if (Math.abs(speed) < 6 || sinceLanding < 0.5) return false;
  return lookahead.some((t, i) => {
    const ground = groundAhead[i];
    if (ground === undefined) return false;
    const flight = height + climb * t - 0.5 * DRIVE.gravity * t * t;
    return ground < flight - margin;
  });
}

/** What the car is driving on. */
export interface Surface {
  /** Multiplies how quickly sideways slip dies away. */
  grip: number;
  /** Rolling resistance, m/s². */
  rolling: number;
  /** Top speed as a fraction of `DRIVE.maxSpeed`. */
  topSpeed: number;
}

export const ROAD: Surface = { grip: 1, rolling: DRIVE.rolling, topSpeed: 1 };
/** Grass, gravel, a field: slower, softer, and the tail comes round sooner. */
export const OFF_ROAD: Surface = { grip: 0.6, rolling: 1.4, topSpeed: 0.5 };

/** Below this speed the car counts as stopped, in m/s. */
const STOP_THRESHOLD = 0.05;

export interface DriveInput {
  /** -1 (reverse/brake) to 1 (throttle). */
  throttle: number;
  /** -1 (left) to 1 (right). */
  steer: number;
  handbrake: boolean;
}

/**
 * What drives the wheels, and how. An electric motor gives all its torque at
 * once and holds it to town speeds, needs no gears, and brakes by
 * regenerating whenever the pedal is lifted. A petrol engine pulls best in
 * the middle of its revs and pauses at every change up; a diesel pulls hard
 * low down, runs out of revs early and lags on its turbo. The heavier the
 * body, the less of any of it.
 */
export interface Powertrain {
  kind: 'electric' | 'petrol' | 'diesel';
  /** Pull as a fraction of a hatchback's: a van's or a pickup's is less. */
  weight: number;
}

/** Per powertrain: peak pull, m/s²; top speed, m/s; engine or regenerative braking, m/s²; throttle lag, s. */
const POWER = {
  electric: { pull: 7, top: 50, coast: 1.9, lag: 0.05 },
  petrol: { pull: 6.3, top: 56, coast: 0.8, lag: 0.25 },
  diesel: { pull: 6, top: 49, coast: 1.05, lag: 0.45 },
} as const;
/** An electric car's full pull holds to here, m/s (about 70 km/h); past it, its power is the limit. */
const EV_TORQUE_TO = 19;
/** Each gear's top speed, m/s, for the automatic's six gears. */
const GEARS = {
  petrol: [13, 22, 31, 40, 48, 56],
  diesel: [11, 19, 27, 35, 42, 49],
} as const;
/** Each gear's top speed, m/s, for a powertrain in a chassis: its own gearbox, or the car's six. For the engine's sound. */
export function gearTops(train: Powertrain | undefined, chassis: Chassis = CAR_CHASSIS): readonly number[] {
  if (train === undefined || train.kind === 'electric') return [];
  return chassis.gears ?? GEARS[train.kind];
}

/** How the pull falls away in the higher gears: the ratio's leverage. */
const GEAR_PULL = [1, 0.74, 0.58, 0.48, 0.41, 0.36];
/** A change up: the pull stops for this long, s. */
const SHIFT_TIME = { petrol: 0.28, diesel: 0.36 } as const;

/**
 * A body's weight against a hatchback's, for how hard its powertrain pulls
 * it: a laden box truck pulls about a third as hard, a laden articulated
 * truck under a fifth, which is what takes it most of a minute to 80 km/h.
 */
export const BODY_WEIGHT: Record<CarKind, number> = {
  hatch: 1, sedan: 0.97, estate: 0.94, suv: 0.84, van: 0.72, pickup: 0.8, truck: 0.3, semi: 0.17,
  busArea: 0.22, busExpress: 0.19, busMetro: 0.22, busCity: 0.22,
};

export interface DriveState {
  /** Signed speed along the car's heading, m/s. */
  speed: number;
  /** The automatic's gear, 1..6, for a car with an engine. */
  gear?: number;
  /** Seconds left of a change up, the pull cut. */
  shifting?: number;
  /** The throttle as the engine has it: the pedal, lagged by the turbo. */
  spool?: number;
  /** Steering angle in radians, smoothed toward the input. */
  steerAngle: number;
  /** Sideways speed, m/s, positive to the right: the car sliding. */
  lateral?: number;
  /** Acceleration along the heading last step, m/s², for the body's pitch. */
  accel?: number;
}

/**
 * The steering the tyres can use at a speed: full lock when crawling, and at
 * speed no more than keeps the sideways acceleration within grip. Turning the
 * wheel further than that only scrubs the front tyres, so the car does not.
 */
export function steerLimit(speed: number, chassis: Chassis = CAR_CHASSIS): number {
  const v2 = Math.max(1, speed * speed);
  return Math.min(chassis.steerLock, Math.atan((chassis.wheelbase * chassis.cornerGrip) / v2));
}

/**
 * Advance the vehicle by `dt`.
 *
 * `slope` is the gradient along the car's heading — positive uphill — taken
 * from the terrain the car is standing on. Returns the new state plus the yaw
 * change to apply (positive clockwise, as the steering).
 */
/**
 * How hard the powertrain pulls at `speed`, m/s², for a pedal already lagged
 * to `spool`, and the gearbox after this step.
 */
function pull(train: Powertrain, speed: number, spool: number, gear: number, shifting: number, dt: number, chassis: Chassis = CAR_CHASSIS):
  { accel: number; gear: number; shifting: number } {
  const v = Math.max(0, speed);
  if (train.kind === 'electric') {
    // Full torque to town speeds, then the motor's power: pull falling as 1/v.
    const a = POWER.electric.pull * Math.min(1, EV_TORQUE_TO / Math.max(v, 1));
    return { accel: a * spool * train.weight, gear: 1, shifting: 0 };
  }
  const tops = chassis.gears ?? GEARS[train.kind];
  // The automatic: up at the top of a gear, down when well below the one before.
  if (shifting > 0) shifting = Math.max(0, shifting - dt);
  if (gear < tops.length && v > tops[gear - 1] * 0.94 && spool > 0.05) { gear++; shifting = chassis.shift ?? SHIFT_TIME[train.kind]; }
  while (gear > 1 && v < tops[gear - 2] * 0.6) gear--;
  if (shifting > 0) return { accel: 0, gear, shifting };
  // Revs through the gear, the clutch slipping off the line.
  const revs = Math.max(gear === 1 ? 0.35 : 0.2, Math.min(1, v / tops[gear - 1]));
  const torque = train.kind === 'petrol'
    ? 0.62 + 0.38 * Math.max(0, 1 - Math.abs(revs - 0.7) / 0.7)
    : revs < 0.45 ? 1 : Math.max(0.5, 1 - (revs - 0.45) * 0.9);
  // A truck: its power over its weight, at this speed, all it can put down
  // off the line; through the gear, the diesel's torque curve.
  if (chassis.power !== undefined) {
    const a = Math.min(chassis.launch ?? 1, chassis.power / Math.max(v, 1));
    return { accel: a * Math.min(1, torque + 0.15) * spool, gear, shifting };
  }
  // A gear's leverage: a car's six, or for a gearbox of its own, its ratio to first, eased.
  const leverage = chassis.gears === undefined ? GEAR_PULL[gear - 1] : Math.pow(tops[0] / tops[gear - 1], 0.62);
  return { accel: POWER[train.kind].pull * leverage * torque * spool * train.weight, gear, shifting };
}

export function stepVehicle(
  state: DriveState,
  input: DriveInput,
  slope: number,
  dt: number,
  surface: Surface = ROAD,
  /** What drives it. Without one, the one pull every car had before powertrains. */
  train?: Powertrain,
  chassis: Chassis = CAR_CHASSIS,
): { state: DriveState; yawDelta: number } {
  const before = state.speed;
  const target = input.steer * steerLimit(state.speed, chassis);
  const rate = Math.abs(target) < Math.abs(state.steerAngle) ? chassis.steerReturn : chassis.steerRate;
  const steerAngle = approach(state.steerAngle, target, rate * dt);

  let speed = state.speed;
  let lateral = state.lateral ?? 0;
  const top = (chassis.top ?? (train === undefined ? DRIVE.maxSpeed : POWER[train.kind].top)) * surface.topSpeed;
  const coast = chassis.coast ?? (train === undefined ? DRIVE.engineBrake : POWER[train.kind].coast);
  // The pedal as the engine feels it: at once for a motor, after a turbo's lag for an engine.
  const lag = train === undefined ? 0 : POWER[train.kind].lag;
  const pedal = Math.max(0, input.throttle);
  const spool = lag === 0 ? pedal : approach(state.spool ?? 0, pedal, dt / lag);
  let gear = state.gear ?? 1;
  let shifting = state.shifting ?? 0;

  if (input.throttle > 0) {
    // The pull fades toward top speed, so it is approached, not hit.
    const fade = Math.max(0, 1 - (Math.max(0, speed) / top) ** 2);
    if (train === undefined) {
      speed += DRIVE.power * input.throttle * fade * dt;
    } else {
      // Air and tyres set where the pull runs out; an electric car is also
      // held to its limit (below). No fade on top: the gears already do it.
      const p = pull(train, speed, spool, gear, shifting, dt, chassis);
      gear = p.gear;
      shifting = p.shifting;
      speed += p.accel * dt;
    }
  } else if (input.throttle < 0) {
    // One pedal: brake while rolling forward, reverse once stopped.
    if (speed > 0.2) speed = Math.max(0, speed - chassis.brake * dt);
    else speed = Math.max(-chassis.reverseMax, speed + DRIVE.power * 0.7 * input.throttle * dt * (chassis === CAR_CHASSIS ? 1 : 0.3));
  } else if (Math.abs(speed) > STOP_THRESHOLD) {
    // Lifting off: engine braking, or an electric car's regeneration — which
    // fades out as it slows, so it rolls to a stop rather than jerking into one.
    const regen = train?.kind === 'electric' ? Math.min(1, Math.abs(speed) / 4) : 1;
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), coast * regen * dt);
  }
  // Coasting, the automatic drops through the gears with the speed.
  if (train !== undefined && train.kind !== 'electric' && input.throttle <= 0) {
    const tops = chassis.gears ?? GEARS[train.kind];
    while (gear > 1 && Math.max(0, speed) < tops[gear - 2] * 0.6) gear--;
    shifting = 0;
  }

  if (input.handbrake && Math.abs(speed) > STOP_THRESHOLD) {
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), DRIVE.handbrake * dt);
  }

  // Gravity along the slope. sin(atan(slope)) rather than the gradient itself,
  // so a vertical cliff costs exactly 1 g instead of diverging.
  const gravityAccel = -DRIVE.gravity * Math.sin(Math.atan(slope));
  const parked = Math.abs(speed) <= STOP_THRESHOLD && input.throttle === 0;
  if (parked && Math.abs(gravityAccel) < DRIVE.parkingHold) {
    // Stopped with no throttle on a slope the parking brake can hold: parked.
    // On anything steeper the car rolls, as it would.
    speed = 0;
  } else {
    speed += gravityAccel * dt;
    // Resistance opposes motion and must not push a slowing car into reverse.
    const rolling = chassis === CAR_CHASSIS ? surface.rolling : surface.rolling * (chassis.rolling / DRIVE.rolling);
    const resist = (rolling + chassis.drag * speed * speed) * dt;
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), resist);
  }

  speed = Math.max(-chassis.reverseMax, Math.min(top * 1.02, speed));

  // Bicycle model: the car rotates about its axle at a rate set by the
  // steering angle and how fast it is travelling. A locked rear lets it
  // rotate faster than its front wheels alone would turn it.
  const spin = input.handbrake && Math.abs(speed) > 4 ? 1.5 : 1;
  const yawDelta = (speed / chassis.wheelbase) * Math.tan(steerAngle) * dt * spin;

  // The car has turned; its momentum has not. Seen from the car, the velocity
  // swings the other way — outward — and the tyres' grip pulls it back in.
  // With good grip that is instant and the car simply follows its wheels;
  // on the handbrake, or on grass, the tail keeps going: a slide.
  lateral -= speed * yawDelta;
  const grip = (input.handbrake ? DRIVE.handbrakeGrip : DRIVE.lateralGrip) * surface.grip;
  lateral *= Math.exp(-grip * dt);
  // A slide scrubs speed.
  speed -= Math.sign(speed) * Math.min(Math.abs(speed), Math.abs(lateral) * 0.35 * dt);
  if (Math.abs(lateral) < 1e-4) lateral = 0;

  return { state: { speed, steerAngle, lateral, accel: dt > 0 ? (speed - before) / dt : 0, gear, shifting, spool }, yawDelta };
}

function approach(current: number, target: number, maxStep: number): number {
  const delta = target - current;
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}

/** Orient a car to sit flat on a slope, given the terrain gradients. */
export function orientToSlope(
  object: Object3D,
  yaw: number,
  forwardSlope: number,
  lateralSlope: number,
): void {
  object.rotation.set(0, 0, 0);
  object.rotateY(yaw);
  // Nose-up on an incline is a negative rotation about X in a Y-up,
  // Z-south frame.
  object.rotateX(Math.atan(forwardSlope));
  // Right side up where the ground is higher on the right: the car's +X is
  // its right, and a positive turn about Z raises it. (This was negated,
  // which leaned every car into the hillside instead of along it.)
  object.rotateZ(Math.atan(lateralSlope));
}

// --- the model ------------------------------------------------------------------

/** The kinds of car there are: the parked ones, and so the drivable ones. */
export type CarKind = 'hatch' | 'sedan' | 'estate' | 'suv' | 'van' | 'pickup' | 'truck' | 'semi' |
  'busArea' | 'busExpress' | 'busMetro' | 'busCity';
/** The trucks: a rigid box truck, and an articulated one, a tractor and its trailer. See `Truck.ts`. */
export type TruckKind = Extract<CarKind, 'truck' | 'semi'>;
export const isTruck = (kind: CarKind): kind is TruckKind => kind === 'truck' || kind === 'semi';
/** The buses: Västtrafik's area, express and metro designs, and a plain one. See `Bus.ts`. */
export type BusKind = Extract<CarKind, 'busArea' | 'busExpress' | 'busMetro' | 'busCity'>;
export const isBus = (kind: CarKind): kind is BusKind => kind.startsWith('bus');
/** The cars proper: neither trucks nor buses. */
export type CarOnlyKind = Exclude<CarKind, TruckKind | BusKind>;

/**
 * One kind of car, drawn in side profile: (z, y) points, z forward-negative.
 * The shell runs from under the nose over the bonnet and roofline to under
 * the tail; the wheel arches are cut from it. The cabin is the glass, and
 * the roof sits over it.
 */
interface CarSpec {
  wheelbase: number;
  wheelRadius: number;
  bodyWidth: number;
  shell: Array<[number, number]>;
  /** Sill height between the arches. */
  sill: number;
  cabin: Array<[number, number]>;
  roof: Array<[number, number]>;
  /** The waistline under the windows: mirrors, handles and the dash sit on it. */
  belt: number;
  frontLamp: number;
  rearLamp: number;
  bumper: number;
  /** Where the driver's hips sit: height and how far back. */
  seat: [number, number];
  roofRails?: boolean;
  spoiler?: boolean;
  /** Four doors (a B pillar and two handles a side) rather than two. */
  fourDoors: boolean;
  /** A van's panelled load area, from this z back. */
  cargoFrom?: number;
  /** A pickup's open bed, from this z back. */
  bedFrom?: number;
}

const CARS: Record<CarOnlyKind, CarSpec> = {
  hatch: {
    wheelbase: 2.68, wheelRadius: 0.33, bodyWidth: 1.8, sill: 0.3,
    shell: [[-2.16, 0.3], [-2.2, 0.52], [-2.12, 0.71], [-1.95, 0.8], [-0.95, 0.93], [1.55, 0.97], [1.98, 0.95], [2.17, 0.86], [2.2, 0.55], [2.15, 0.3]],
    cabin: [[-0.93, 0.92], [-0.27, 1.41], [1.12, 1.43], [1.9, 0.98], [1.9, 0.92]],
    roof: [[-0.34, 1.4], [1.18, 1.42], [1.2, 1.49], [-0.3, 1.48]],
    belt: 0.95, frontLamp: 0.67, rearLamp: 0.76, bumper: 0.35, seat: [0.52, 0.22],
    roofRails: true, spoiler: true, fourDoors: true,
  },
  sedan: {
    wheelbase: 2.82, wheelRadius: 0.33, bodyWidth: 1.8, sill: 0.3,
    shell: [[-2.33, 0.3], [-2.37, 0.5], [-2.3, 0.68], [-2.1, 0.78], [-1.05, 0.92], [1.55, 0.98], [2.2, 0.98], [2.35, 0.9], [2.37, 0.55], [2.32, 0.3]],
    cabin: [[-1.03, 0.91], [-0.3, 1.38], [0.95, 1.4], [1.62, 0.99], [1.62, 0.93]],
    roof: [[-0.37, 1.37], [1.0, 1.39], [1.02, 1.46], [-0.33, 1.45]],
    belt: 0.96, frontLamp: 0.66, rearLamp: 0.8, bumper: 0.35, seat: [0.5, 0.2], fourDoors: true,
  },
  estate: {
    wheelbase: 2.82, wheelRadius: 0.33, bodyWidth: 1.82, sill: 0.3,
    shell: [[-2.35, 0.3], [-2.39, 0.5], [-2.32, 0.68], [-2.12, 0.78], [-1.05, 0.92], [1.9, 0.98], [2.3, 0.96], [2.38, 0.86], [2.39, 0.55], [2.34, 0.3]],
    cabin: [[-1.03, 0.91], [-0.3, 1.42], [1.95, 1.44], [2.28, 1.0], [2.28, 0.93]],
    roof: [[-0.37, 1.41], [2.0, 1.43], [2.04, 1.5], [-0.33, 1.49]],
    belt: 0.96, frontLamp: 0.66, rearLamp: 0.8, bumper: 0.35, seat: [0.5, 0.2], roofRails: true, fourDoors: true,
  },
  suv: {
    wheelbase: 2.75, wheelRadius: 0.38, bodyWidth: 1.9, sill: 0.4,
    shell: [[-2.28, 0.4], [-2.32, 0.62], [-2.25, 0.86], [-2.05, 0.98], [-1.0, 1.08], [1.7, 1.12], [2.15, 1.1], [2.3, 1.0], [2.32, 0.65], [2.27, 0.4]],
    cabin: [[-0.98, 1.07], [-0.3, 1.66], [1.75, 1.68], [2.12, 1.14], [2.12, 1.08]],
    roof: [[-0.37, 1.65], [1.82, 1.67], [1.85, 1.75], [-0.33, 1.74]],
    belt: 1.1, frontLamp: 0.84, rearLamp: 0.92, bumper: 0.47, seat: [0.68, 0.2], roofRails: true, spoiler: true, fourDoors: true,
  },
  van: {
    wheelbase: 3.0, wheelRadius: 0.34, bodyWidth: 2.0, sill: 0.32,
    shell: [[-2.46, 0.32], [-2.5, 0.55], [-2.44, 0.8], [-2.2, 0.95], [-1.55, 1.05], [2.45, 1.08], [2.5, 1.0], [2.5, 0.4], [2.45, 0.32]],
    cabin: [[-1.55, 1.04], [-1.0, 1.84], [2.45, 1.88], [2.49, 1.84], [2.49, 1.06]],
    roof: [[-1.05, 1.83], [2.5, 1.87], [2.5, 1.96], [-1.0, 1.94]],
    belt: 1.06, frontLamp: 0.8, rearLamp: 0.95, bumper: 0.38, seat: [0.72, -0.35], fourDoors: false, cargoFrom: -0.3,
  },
  pickup: {
    wheelbase: 3.1, wheelRadius: 0.38, bodyWidth: 1.95, sill: 0.42,
    shell: [[-2.58, 0.42], [-2.62, 0.64], [-2.55, 0.9], [-2.3, 1.02], [-1.3, 1.1], [2.55, 1.1], [2.62, 1.04], [2.62, 0.6], [2.57, 0.42]],
    cabin: [[-1.28, 1.09], [-0.65, 1.68], [0.55, 1.7], [0.62, 1.12], [0.62, 1.1]],
    roof: [[-0.7, 1.67], [0.62, 1.69], [0.62, 1.77], [-0.66, 1.76]],
    belt: 1.12, frontLamp: 0.86, rearLamp: 0.9, bumper: 0.5, seat: [0.72, 0.1], fourDoors: false, bedFrom: 0.7,
  },
};

export const CAR_KINDS = Object.keys(CARS) as CarKind[];

/**
 * The top of a car's roof: how high, metres, and the middle of it along the
 * car (z, back positive), for what stands on it: a police car's light bar.
 */
export function roofTop(kind: CarOnlyKind): { y: number; z: number } {
  const roof = CARS[kind].roof;
  const y = Math.max(...roof.map(([, h]) => h));
  const zs = roof.map(([z]) => z);
  return { y, z: (Math.min(...zs) + Math.max(...zs)) / 2 };
}

/** How high the driver's eyes are in each kind, for the first-person camera: high in a truck's cab. */
export function carEyeHeight(kind: CarKind): number {
  if (isTruck(kind)) return TRUCK_EYE;
  if (isBus(kind)) return BUS_EYE;
  return CARS[kind].seat[0] + 0.72;
}

/** A truck driver's eyes over the road, metres, and a bus driver's. */
const TRUCK_EYE = 2.65;
const BUS_EYE = 2.0;

export const faceted = (color: number, roughness: number, metalness = 0, extra: Partial<MeshStandardMaterial> = {}) => {
  const m = new MeshStandardMaterial({ color: new Color(color), roughness, metalness, flatShading: true });
  Object.assign(m, extra);
  if (extra.transparent !== true) applyStreetLampLight(m);
  return m;
};

export const trim = faceted(0x2a2d33, 0.7);
/** See-through, so whoever is driving can be seen inside. */
export const glass = faceted(0x2d4660, 0.08, 0.5, { transparent: true, opacity: 0.38, depthWrite: false });
export const chrome = faceted(0xd6d9de, 0.28, 0.85);
const tyre = faceted(0x1c1c1f, 0.95);
const rim = faceted(0xc9ccd2, 0.3, 0.8);
const rimShadow = faceted(0x55595f, 0.6, 0.4);
export const plate = faceted(0xf4f4ef, 0.6);
export const plateBlue = faceted(0x2350a8, 0.6);
export const upholstery = faceted(0x33363c, 0.9);
export const dash = faceted(0x25272b, 0.8);
export const headlamp = faceted(0xfff4df, 0.2, 0.1, { emissive: new Color(0xfff0c8), emissiveIntensity: 0.4 });
export const taillamp = faceted(0xb3161b, 0.3, 0.1, { emissive: new Color(0xff2a1a), emissiveIntensity: 0.5 });
export const indicator = faceted(0xf2a33a, 0.3, 0.1, { emissive: new Color(0xff9a2a), emissiveIntensity: 0.3 });

/** One piece of the car. */
export function part(geometry: BufferGeometry, material: Material, x = 0, y = 0, z = 0): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.position.set(x, y, z);
  mesh.castShadow = material !== glass;
  mesh.receiveShadow = true;
  return mesh;
}

/** A piece of the cabin's inside: seen only through the glass, and left out of the parked cars. */
export function inside(geometry: BufferGeometry, material: Material, x = 0, y = 0, z = 0): Mesh {
  const mesh = part(geometry, material, x, y, z);
  mesh.userData.interior = true;
  mesh.castShadow = false;
  return mesh;
}

/**
 * A side profile, extruded across the car and centred on it, with chamfered
 * edges. `points` are (z, y) pairs, z forward-negative like the car.
 */
function extrudeProfile(outline: Shape, width: number, bevel: number): ExtrudeGeometry {
  const geometry = new ExtrudeGeometry(outline, {
    depth: width - 2 * bevel,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 1,
    curveSegments: 7,
  });
  // Shape space is (z, y) and the extrusion runs along its Z: turn it so the
  // profile stands along the car and the extrusion runs across it.
  geometry.translate(0, 0, -(width - 2 * bevel) / 2);
  geometry.rotateY(-Math.PI / 2);
  return geometry;
}

/** The cabin's inside, seen from within: its faces point in. */
const cabinTub = new MeshStandardMaterial({ color: new Color(0x1d1f23), roughness: 0.95, side: BackSide });

/**
 * The profile with a point on its top edge at each z in `at`, so the
 * extrusion's top faces break there.
 */
function withTopPoints(points: ReadonlyArray<readonly [number, number]>, at: readonly number[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const top = Math.max(...points.map(p => p[1])) - 0.35;
  for (let i = 0; i < points.length; i++) {
    out.push([points[i][0], points[i][1]]);
    const next = points[i + 1];
    if (next === undefined || points[i][1] < top || next[1] < top) continue;
    const [za, ya] = points[i], [zb, yb] = next;
    for (const z of [...at].sort((a, b) => (zb > za ? a - b : b - a))) {
      if ((z - za) * (z - zb) >= 0) continue;
      out.push([z, ya + ((yb - ya) * (z - za)) / (zb - za)]);
    }
  }
  return out;
}

/** The height of a body profile's top edge at `z`, metres: where it is highest there. */
function topOf(points: ReadonlyArray<readonly [number, number]>, z: number): number {
  let best = -Infinity;
  for (let i = 0; i + 1 < points.length; i++) {
    const [za, ya] = points[i], [zb, yb] = points[i + 1];
    if ((z - za) * (z - zb) > 0 || za === zb) continue;
    best = Math.max(best, ya + ((yb - ya) * (z - za)) / (zb - za));
  }
  return best;
}

/**
 * Take the body's top away over the cabin: the upward faces between the
 * cabin's ends and inside its width. The sides, the bonnet and the tail stay.
 */
function cutCabin(geometry: BufferGeometry, [z0, z1]: readonly [number, number], width: number, belt: number): void {
  const flat = geometry.index === null ? geometry : geometry.toNonIndexed();
  const keep: number[] = [];
  const p = flat.getAttribute('position');
  const attrs = Object.entries(flat.attributes);
  for (let t = 0; t < p.count; t += 3) {
    let cx = 0, cy = 0, cz = 0;
    for (let k = 0; k < 3; k++) { cx += p.getX(t + k); cy += p.getY(t + k); cz += p.getZ(t + k); }
    cx /= 3; cy /= 3; cz /= 3;
    // The face's normal: up, for the body's top.
    const ux = p.getX(t + 1) - p.getX(t), uy = p.getY(t + 1) - p.getY(t), uz = p.getZ(t + 1) - p.getZ(t);
    const vx = p.getX(t + 2) - p.getX(t), vy = p.getY(t + 2) - p.getY(t), vz = p.getZ(t + 2) - p.getZ(t);
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const up = Math.abs(ny) / (Math.hypot(nx, ny, nz) || 1) > 0.6;
    const over = up && cy > belt - 0.2 && cz > z0 && cz < z1 && Math.abs(cx) < width / 2;
    if (!over) keep.push(t);
  }
  for (const [name, attr] of attrs) {
    const size = attr.itemSize;
    const src = attr.array as Float32Array;
    const dst = new Float32Array(keep.length * 3 * size);
    keep.forEach((t, i) => dst.set(src.subarray(t * size, (t + 3) * size), i * 3 * size));
    geometry.setAttribute(name, new BufferAttribute(dst, size));
  }
  geometry.setIndex(null);
  if (flat !== geometry) flat.dispose();
}

function profile(points: Array<[number, number]>): Shape {
  const shape = new Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) shape.lineTo(points[i][0], points[i][1]);
  shape.closePath();
  return shape;
}

/**
 * How far forward (-1) or back (+1) a geometry reaches near a height: the
 * surface a lamp or a plate is mounted on.
 */
function surfaceZ(geometry: BufferGeometry, y: number, end: 1 | -1): number {
  const p = geometry.getAttribute('position');
  let best = 0;
  for (let i = 0; i < p.count; i++) {
    if (Math.abs(p.getY(i) - y) > 0.22) continue;
    const z = p.getZ(i);
    if (z * end > best * end) best = z;
  }
  return best;
}

/** A thin bar between two points on one side of the car: a pillar. */
export function strut(material: Material, x: number, z0: number, y0: number, z1: number, y1: number, depth: number, width: number): Mesh {
  const length = Math.hypot(z1 - z0, y1 - y0);
  const mesh = part(new BoxGeometry(width, length, depth), material, x, (y0 + y1) / 2, (z0 + z1) / 2);
  // Stand it up along the segment: tilt about X from vertical toward the car's Z.
  mesh.rotation.x = Math.atan2(z1 - z0, y1 - y0);
  return mesh;
}

/** A wheel: rounded tyre, five-spoke rim, hub. Spins about its X axis. */
export function buildWheel(outward: 1 | -1, r: number): Group {
  const w = VEHICLE.tyreWidth;
  const wheel = new Group();
  // The tyre, turned in section: a rounded shoulder, faceted.
  const section = [
    new Vector2(r * 0.62, -w / 2), new Vector2(r * 0.9, -w / 2), new Vector2(r, -w * 0.28),
    new Vector2(r, w * 0.28), new Vector2(r * 0.9, w / 2), new Vector2(r * 0.62, w / 2),
  ];
  const tyreGeometry = new LatheGeometry(section, 14);
  tyreGeometry.rotateZ(Math.PI / 2);
  wheel.add(part(tyreGeometry, tyre));
  // The rim: a dished disc, spokes proud of it, and the hub.
  const disc = new CylinderGeometry(r * 0.62, r * 0.62, w * 0.7, 14);
  disc.rotateZ(Math.PI / 2);
  wheel.add(part(disc, rimShadow));
  const face = w * 0.35 * outward;
  for (let i = 0; i < 5; i++) {
    const spoke = part(new BoxGeometry(0.035, r * 0.56, 0.06), rim, face, 0, 0);
    spoke.rotation.x = (i / 5) * Math.PI * 2;
    spoke.geometry.translate(0, r * 0.28, 0);
    wheel.add(spoke);
  }
  const hub = new CylinderGeometry(r * 0.16, r * 0.18, 0.05, 8);
  hub.rotateZ(Math.PI / 2);
  wheel.add(part(hub, chrome, face + 0.02 * outward, 0, 0));
  return wheel;
}

export interface VehicleWheel {
  /** Turns for steering (front wheels only), about Y. */
  pivot: Group;
  /** Spins with the road, about X. */
  spin: Group;
  front: boolean;
  /** Position on the car, metres: x right, z forward-negative. */
  x: number;
  z: number;
  /** Knocked out of line by a crash: its toe, radians, and how far its hub has dropped, metres. */
  toe?: number;
  drop?: number;
}

// --- the instrument cluster -----------------------------------------------------
//
// One display for every car: only the one being driven shows it, so one
// canvas, redrawn a few times a second, is all the instruments there are.

const CLUSTER_W = 512, CLUSTER_H = 176;
const clusterCanvas = typeof document === 'undefined' ? undefined : document.createElement('canvas');
if (clusterCanvas !== undefined) { clusterCanvas.width = CLUSTER_W; clusterCanvas.height = CLUSTER_H; }
const clusterTexture = clusterCanvas === undefined ? undefined : new CanvasTexture(clusterCanvas);
if (clusterTexture !== undefined) clusterTexture.colorSpace = SRGBColorSpace;
export const clusterMaterial = new MeshBasicMaterial({ map: clusterTexture, toneMapped: false });
// Narrow enough to be read inside the wheel's rim.
export const clusterGeometry = new PlaneGeometry(0.27, 0.093);

export interface ClusterReading {
  /** km/h. */
  speed: number;
  /** 0..1 of the tank or battery. */
  fraction: number;
  electric: boolean;
  /** km left. */
  range: number;
  /** The gear, or D for an electric car. */
  gear: string;
}

let lastCluster = '';

/** Draw the instruments: speed, and the fuel or charge left and how far it goes. */
export function drawCluster(r: ClusterReading): void {
  if (clusterCanvas === undefined || clusterTexture === undefined) return;
  const key = `${Math.round(r.speed)}|${Math.round(r.fraction * 100)}|${Math.round(r.range)}|${r.electric}|${r.gear}`;
  if (key === lastCluster) return;
  lastCluster = key;
  const ctx = clusterCanvas.getContext('2d')!;
  const w = CLUSTER_W, h = CLUSTER_H;
  ctx.fillStyle = '#0b0d10';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = '#2a2f36';
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, w - 6, h - 6);
  const font = (px: number, weight = 700) => `${weight} ${px}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

  // Speed, big, on the left.
  ctx.fillStyle = '#f2f4f5';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = font(92);
  ctx.fillText(String(Math.round(Math.abs(r.speed))), 110, 118);
  ctx.font = font(24, 600);
  ctx.fillStyle = '#9aa3ab';
  ctx.fillText('km/h', 110, 152);
  // The gear, or D, boxed between the speed and the fuel: the display's
  // corners are behind the wheel's rim from the driver's seat.
  const gx = 222, gy = 58, gw = 50, gh = 62;
  ctx.strokeStyle = r.electric ? '#3fd0ff' : '#f5b83d';
  ctx.lineWidth = 3;
  ctx.strokeRect(gx - gw / 2, gy, gw, gh);
  ctx.font = font(44);
  ctx.fillStyle = r.electric ? '#3fd0ff' : '#f5b83d';
  ctx.textAlign = 'center';
  ctx.fillText(r.gear, gx, gy + 47);

  // Fuel or charge: a bar of segments, the percentage, and the range.
  const low = r.fraction < 0.12;
  const lit = low ? '#ff5a3c' : r.electric ? '#3fd0ff' : '#f5b83d';
  const x0 = 270, y0 = 36, segW = 20, gap = 4;
  ctx.font = font(30);
  ctx.textAlign = 'left';
  ctx.fillStyle = lit;
  ctx.fillText(r.electric ? '⚡' : '⛽', x0 - 6, y0 + 26);
  const segs = 8;
  for (let i = 0; i < segs; i++) {
    ctx.fillStyle = (i + 0.5) / segs <= r.fraction ? lit : '#262b31';
    ctx.fillRect(x0 + 34 + i * (segW + gap), y0, segW, 30);
  }
  ctx.font = font(26, 600);
  ctx.fillStyle = '#f2f4f5';
  ctx.fillText(`${Math.round(r.fraction * 100)} %`, x0 + 34, y0 + 72);
  ctx.fillStyle = low ? '#ff5a3c' : '#c9d0d6';
  ctx.font = font(30);
  ctx.fillText(`${Math.round(r.range)} km`, x0 + 34, y0 + 116);
  ctx.font = font(18, 600);
  ctx.fillStyle = '#7d868e';
  ctx.fillText('RANGE', x0 + 150, y0 + 116);
  clusterTexture.needsUpdate = true;
}

/** The tailpipe, shared by every car that has one. */
export const exhaustGeometry = new CylinderGeometry(0.045, 0.045, 0.3, 10).rotateX(Math.PI / 2);
export const exhaustMaterial = chrome;

const exhaustSpots = new Map<CarKind, Vector3>();

/** Where a car's tailpipe is, in its root's frame: for putting pipes on the parked ones. */
export function exhaustSpot(kind: CarKind): Vector3 {
  let at = exhaustSpots.get(kind);
  if (at === undefined) {
    const car = createVehicle(kind);
    car.root.updateMatrixWorld(true);
    at = new Vector3();
    car.root.traverse(o => { if (o instanceof Mesh && o.geometry === exhaustGeometry) o.getWorldPosition(at!); });
    exhaustSpots.set(kind, at);
  }
  return at;
}

export interface VehicleModel {
  kind: CarKind;
  root: Group;
  /** The sprung body, which pitches and rolls over the wheels. */
  body: Group;
  wheels: VehicleWheel[];
  wheelRadius: number;
  /** A beam from each headlamp, left then right, lit at night. */
  headlights: [SpotLight, SpotLight];
  /** The body colour: this car's own material. */
  paint: MeshStandardMaterial;
  /** The driver's door, hinged at its front edge: turn it about Y to open it (negative opens). Hidden while shut. */
  door: Group;
  /** Where the driver sits and reaches, in the body's frame: hips, wheel rim, pedals, and where to stand outside the door. */
  seat: { hipY: number; hipZ: number; wheelY: number; wheelZ: number; pedalY: number; pedalZ: number; x: number; outsideX: number };
  /** The instrument cluster behind the wheel: shown in the car being driven. See `drawCluster`. */
  cluster: Mesh;
  /** An articulated truck's trailer, if it has one. See `Truck.ts`. */
  trailer?: TrailerModel;
}

/**
 * A semi-trailer, hitched to its tractor at the kingpin: its root sits there,
 * turned about it by the articulation (`root.rotation.y`), the trailer
 * running back along +z from it.
 */
export interface TrailerModel {
  root: Group;
  wheels: VehicleWheel[];
  wheelRadius: number;
  /** Where the kingpin sits on the tractor, its z (forward-negative). */
  kingpinZ: number;
  /** How far back from the kingpin the trailer's axles turn it, metres. */
  axleZ: number;
  /** From the kingpin to its tail, metres, and its width. */
  length: number;
  width: number;
}

/**
 * Build a car of a kind and colour. The root's origin is on the ground at the
 * car's centre, so placing it is "put it where the road is".
 */
export function createVehicle(kind: CarKind = 'hatch', colour = 0xe0503f,
  /** Electric: no exhaust pipe, which is how one is told from a petrol car at a glance. */
  electric = false,
  /**
   * The cabin open down to its floor, for someone to sit in: without it the
   * body is solid to the window line, and its top shows through the glass as
   * a plane across the driver. Parked cars, with nobody in them, stay solid.
   */
  open = true): VehicleModel {
  if (isTruck(kind)) return createTruck(kind, colour, open);
  if (isBus(kind)) return createBus(kind, colour, open);
  const spec = CARS[kind];
  const root = new Group();
  const body = new Group();
  root.add(body);
  const paint = faceted(colour, 0.32, 0.25);
  const half = spec.bodyWidth / 2;
  const wr = spec.wheelRadius;
  const axle = spec.wheelbase / 2;
  const arch = wr + 0.09;

  // --- the shell: bonnet, wings, doors and tail in one profile ---------------
  // Where the cabin opens, along the car: from just behind the windscreen's
  // foot to the back of the glass. The profile gets a point at each on its
  // top edge, so the opening's ends are straight across.
  const cabinEnds: [number, number] = [spec.cabin[0][0] + 0.05, spec.cabin[spec.cabin.length - 1][0] - 0.05];
  const outline = open ? withTopPoints(spec.shell, cabinEnds) : spec.shell;
  const shell = new Shape();
  shell.moveTo(outline[0][0], outline[0][1]);
  for (const [z, y] of outline.slice(1)) shell.lineTo(z, y);
  shell.lineTo(axle + arch, spec.sill);
  // Wheel arches, cut up into the sills.
  shell.absarc(axle, wr, arch, 0, Math.PI, false);
  shell.lineTo(-axle + arch, spec.sill - 0.03);
  shell.absarc(-axle, wr, arch, 0, Math.PI, false);
  shell.closePath();
  const shellGeometry = extrudeProfile(shell, spec.bodyWidth, 0.05);
  if (open) {
    cutCabin(shellGeometry, cabinEnds, spec.bodyWidth - 0.3, spec.belt);
    // Inside, a dark tub: floor and walls, so the opening shows a car's inside,
    // not through it. Out to just inside the body's sides: the cut takes the
    // top away across the whole width, and a narrower tub left a slot each
    // side that looked down through the car to the road.
    const floor = spec.sill + 0.12;
    const tub = new Mesh(new BoxGeometry(spec.bodyWidth - 0.1, spec.belt - floor, cabinEnds[1] - cabinEnds[0]), cabinTub);
    tub.position.set(0, (spec.belt + floor) / 2 - 0.01, (cabinEnds[0] + cabinEnds[1]) / 2);
    body.add(tub);
    // And the body's top put back outside the glass: a shoulder each side,
    // from under the glass's edge out to the side, along the cut's length,
    // following the body's top line.
    const [z0, z1] = cabinEnds;
    const inner = (spec.bodyWidth - 0.3) / 2 - 0.03, outer = spec.bodyWidth / 2 - 0.02;
    for (const side of [-1, 1] as const) {
      body.add(strut(paint, side * (inner + outer) / 2, z0, topOf(outline, z0) - 0.012, z1, topOf(outline, z1) - 0.012, 0.024, outer - inner));
    }
  }
  body.add(part(shellGeometry, paint));
  // Where the finished body's nose and tail are at a height: the bevel grows
  // the profile, so anything mounted on them is placed against the real
  // surface rather than the drawing.
  const face = (y: number, end: 1 | -1) => surfaceZ(shellGeometry, y, end);

  // --- the cabin: glass, then its frame and roof over it --------------------
  const cabinWidth = spec.bodyWidth - 0.3;
  body.add(part(extrudeProfile(profile(spec.cabin), cabinWidth, 0.04), glass));
  body.add(part(extrudeProfile(profile(spec.roof), spec.bodyWidth - 0.24, 0.03), paint));
  const [c0, c1, c2, c3] = spec.cabin;
  if (spec.spoiler === true) {
    // A lip spoiler over the tail glass, with the high brake light under it.
    const lip = profile([[c2[0] - 0.02, c2[1] + 0.01], [c2[0] + 0.24, c2[1] - 0.03], [c2[0] + 0.26, c2[1] + 0.01], [c2[0], c2[1] + 0.07]]);
    body.add(part(extrudeProfile(lip, cabinWidth - 0.06, 0.02), paint));
    body.add(part(new BoxGeometry(0.34, 0.03, 0.03), taillamp, 0, c2[1] - 0.04, c2[0] + 0.22));
  }
  if (spec.cargoFrom !== undefined) {
    // The van's load area: panelled sides over the glass behind the cab.
    const length = c3[0] - spec.cargoFrom;
    body.add(part(new BoxGeometry(cabinWidth + 0.03, c2[1] - spec.belt - 0.04, length),
      paint, 0, (c2[1] + spec.belt) / 2, spec.cargoFrom + length / 2));
  }
  if (spec.bedFrom !== undefined) {
    // The pickup's bed: sides and a tailgate round a dark liner.
    const top = spec.shell[5][1];
    const length = spec.shell[6][0] - spec.bedFrom - 0.06;
    for (const side of [-1, 1]) body.add(part(new BoxGeometry(0.07, 0.42, length), paint, side * (half - 0.04), top + 0.21, spec.bedFrom + length / 2));
    body.add(part(new BoxGeometry(spec.bodyWidth - 0.08, 0.42, 0.07), paint, 0, top + 0.21, spec.bedFrom + length));
    body.add(part(new BoxGeometry(spec.bodyWidth - 0.16, 0.04, length - 0.1), trim, 0, top + 0.02, spec.bedFrom + length / 2));
  }
  // Where the front door ends: at the B pillar of a four-door, at the back
  // of a two-door's cab, or where a van's load area begins.
  const bZ = spec.cargoFrom ?? (spec.fourDoors ? (c1[0] + c2[0]) / 2 + 0.1 : c2[0] - 0.08);
  for (const side of [-1, 1] as const) {
    const x = side * (cabinWidth / 2 - 0.005);
    // A pillar up the windscreen, B pillar between the doors, the pillar down
    // the tail, and the waist rail under the windows.
    body.add(strut(paint, x, c0[0], c0[1] + 0.01, c1[0] - 0.01, c1[1], 0.07, 0.05));
    if (spec.fourDoors) body.add(strut(paint, x, bZ, spec.belt, bZ - 0.02, c1[1] + 0.01, 0.12, 0.05));
    if (spec.cargoFrom === undefined) body.add(strut(paint, x, c2[0] + 0.02, c2[1], c3[0] - 0.04, c3[1] + 0.02, 0.09, 0.05));
    body.add(strut(trim, side * (cabinWidth / 2 + 0.01), c0[0] + 0.03, spec.belt, c3[0] - 0.2, spec.belt + 0.04, 0.035, 0.03));
    // Mirrors on stalks at the base of the A pillar.
    body.add(part(new BoxGeometry(0.16, 0.12, 0.2), paint, side * (half + 0.06), spec.belt + 0.05, c0[0] + 0.21));
    // Door shut lines and handles; the driver's front door is its own part.
    const rearDoorEnd = spec.fourDoors ? Math.min(c3[0] - 0.55, bZ + 0.95) : undefined;
    for (const z of [bZ, ...(rearDoorEnd === undefined ? [] : [rearDoorEnd])]) {
      body.add(part(new BoxGeometry(0.012, spec.belt - spec.sill - 0.08, 0.02), trim, side * (half + 0.003), (spec.belt + spec.sill) / 2, z));
    }
    if (spec.fourDoors) body.add(part(new BoxGeometry(0.03, 0.035, 0.16), chrome, side * (half + 0.01), spec.belt - 0.11, bZ + 0.62));
    if (side > 0) body.add(part(new BoxGeometry(0.03, 0.035, 0.16), chrome, half + 0.01, spec.belt - 0.11, bZ - 0.45));
    if (spec.roofRails === true) {
      const r0 = spec.roof[0][0] + 0.1, r1 = spec.roof[1][0] - 0.05;
      body.add(part(new BoxGeometry(0.05, 0.05, r1 - r0), trim, side * (cabinWidth / 2 - 0.15), spec.roof[2][1] + 0.04, (r0 + r1) / 2));
    }
    // Dark cladding along the sills, between the arches.
    body.add(part(new BoxGeometry(0.03, 0.12, spec.wheelbase - 2 * arch - 0.1), trim, side * (half + 0.012), spec.sill + 0.04, 0));
  }

  // --- the driver's door: a panel hinged at its front edge ------------------
  const doorFront = c0[0] + 0.02, doorBack = bZ - 0.01;
  // Drawn only while it swings: shut, the body's own side is the door, and a
  // panel over it would stand proud of it.
  const door = new Group();
  door.visible = false;
  door.position.set(-half - 0.012, 0, doorFront);
  const doorLength = doorBack - doorFront;
  door.add(part(new BoxGeometry(0.04, spec.belt - spec.sill - 0.06, doorLength), paint, 0, (spec.belt + spec.sill) / 2, doorLength / 2));
  door.add(part(new BoxGeometry(0.03, 0.035, 0.16), chrome, -0.03, spec.belt - 0.11, doorLength - 0.2));
  // Its window and frame swing out with it. The window is the shape of the
  // side glass it covers: its front edge leans back along the windscreen
  // pillar, so nothing of it stands proud ahead of the windscreen.
  const frameTop = c1[1] - 0.04;
  // Where the A pillar is at a height, in the door's frame (z from its hinge).
  const pillarZ = (y: number) => c0[0] + (c1[0] - c0[0]) * ((y - c0[1]) / (c1[1] - c0[1])) - doorFront;
  const frontLow = Math.max(0.02, pillarZ(spec.belt) + 0.03), frontHigh = pillarZ(frameTop) + 0.03;
  const back = doorLength - 0.02;
  const pane = extrudeProfile(profile([[frontLow, spec.belt], [back, spec.belt], [back, frameTop], [frontHigh, frameTop]]), 0.02, 0.005);
  door.add(part(pane, glass, 0.01));
  // The frame: along the top, and down the slanted front edge.
  door.add(strut(paint, 0.01, frontHigh, frameTop, back, frameTop, 0.04, 0.035));
  door.add(strut(paint, 0.01, frontLow, spec.belt, frontHigh, frameTop, 0.04, 0.035));
  body.add(door);

  // --- inside: seats, the dash and the wheel, seen through the glass ---------
  const [hipY, hipZ] = spec.seat;
  const seatX = VEHICLE.seatOffset * (spec.bodyWidth / VEHICLE.bodyWidth);
  const wheelY = spec.belt + 0.02, wheelZ = hipZ - 0.62;
  for (const x of [seatX, -seatX]) {
    body.add(inside(new BoxGeometry(0.5, 0.12, 0.5), upholstery, x, hipY - 0.08, hipZ - 0.05));
    const back = inside(new BoxGeometry(0.48, 0.62, 0.12), upholstery, x, hipY + 0.28, hipZ + 0.24);
    back.rotation.x = 0.2;
    body.add(back);
    body.add(inside(new BoxGeometry(0.24, 0.16, 0.1), upholstery, x, hipY + 0.68, hipZ + 0.33));
  }
  if (spec.fourDoors) body.add(inside(new BoxGeometry(cabinWidth - 0.2, 0.55, 0.14), upholstery, 0, hipY + 0.25, hipZ + 1.05));
  body.add(inside(new BoxGeometry(cabinWidth - 0.04, 0.16, 0.42), dash, 0, spec.belt - 0.04, c0[0] + 0.35));
  const rimGeometry = new TorusGeometry(0.17, 0.025, 5, 12);
  const steering = inside(rimGeometry, dash, seatX, wheelY, wheelZ);
  steering.rotation.x = -0.45;
  body.add(steering);
  // The instruments, read through the top of the wheel from the driver's seat.
  const cluster = new Mesh(clusterGeometry, clusterMaterial);
  cluster.position.set(seatX, wheelY + 0.07, wheelZ - 0.16);
  cluster.rotation.x = -0.3;
  cluster.userData.interior = true;
  cluster.visible = false;
  body.add(cluster);

  // --- the front: bumper, grille, lamps, plate ------------------------------
  const fl = spec.frontLamp, rl = spec.rearLamp, bumper = spec.bumper;
  body.add(part(new BoxGeometry(spec.bodyWidth + 0.02, 0.17, 0.16), trim, 0, bumper, face(bumper, -1) + 0.02));
  body.add(part(new BoxGeometry(0.74, 0.15, 0.04), trim, 0, fl - 0.11, face(fl - 0.11, -1) - 0.01));
  for (const side of [-1, 1] as const) {
    body.add(part(new BoxGeometry(0.36, 0.12, 0.06), headlamp, side * (half - 0.33), fl, face(fl, -1) - 0.012));
    body.add(part(new BoxGeometry(0.1, 0.05, 0.06), indicator, side * (half - 0.09), fl - 0.07, face(fl - 0.07, -1) - 0.01));
  }
  body.add(part(new BoxGeometry(0.52, 0.11, 0.02), plate, 0, bumper + 0.09, face(bumper, -1) - 0.07));
  body.add(part(new BoxGeometry(0.05, 0.11, 0.022), plateBlue, -0.235, bumper + 0.09, face(bumper, -1) - 0.072));

  // --- the back: bumper, lamps, plate, exhaust -------------------------------
  body.add(part(new BoxGeometry(spec.bodyWidth + 0.02, 0.18, 0.16), trim, 0, bumper + 0.01, face(bumper, 1) - 0.02));
  for (const side of [-1, 1] as const) {
    // Proud of the tail by a finger's width, as a lamp cluster stands.
    body.add(part(new BoxGeometry(0.34, 0.15, 0.06), taillamp, side * (half - 0.27), rl, face(rl, 1) + 0.012));
    body.add(part(new BoxGeometry(0.1, 0.15, 0.06), indicator, side * (half - 0.045), rl, face(rl, 1) + 0.01));
  }
  body.add(part(new BoxGeometry(0.52, 0.11, 0.02), plate, 0, bumper + 0.23, face(bumper + 0.23, 1) + 0.012));
  body.add(part(new BoxGeometry(0.05, 0.11, 0.022), plateBlue, -0.235, bumper + 0.23, face(bumper + 0.23, 1) + 0.014));
  // Out past the bumper a hand's width, as tailpipes stick out: the tell of a car that burns fuel.
  if (!electric) body.add(part(exhaustGeometry, chrome, 0.5, bumper - 0.1, face(bumper, 1) + 0.07));

  // --- wheels ----------------------------------------------------------------
  const wheels: VehicleWheel[] = [];
  const x = half - 0.115;
  for (const [side, z, front] of [[-1, -axle, true], [1, -axle, true], [-1, axle, false], [1, axle, false]] as const) {
    const pivot = new Group();
    pivot.position.set(side * x, wr, z);
    const spin = buildWheel(side, wr);
    pivot.add(spin);
    root.add(pivot);
    wheels.push({ pivot, spin, front, x: side * x, z });
  }

  // --- the headlight beams, used at night: one from each lamp ----------------
  // Each is aimed a little out from straight ahead, as dipped beams are, so
  // the two pools overlap in the middle of the road and light the verges.
  const headlights = ([-1, 1] as const).map((side) => {
    const beam = new SpotLight(0xfff0d0, 0, 60, 0.42, 0.6, 1.4);
    const x = side * (half - 0.33);
    beam.position.set(x, fl, face(fl, -1) - 0.02);
    beam.target.position.set(x + side * 1.5, 0, -22);
    body.add(beam, beam.target);
    return beam;
  }) as [SpotLight, SpotLight];

  return {
    kind, root, body, wheels, wheelRadius: wr, headlights, paint, door, cluster,
    seat: { hipY, hipZ, wheelY, wheelZ, pedalY: spec.sill + 0.02, pedalZ: c0[0] - 0.1, x: seatX, outsideX: -half - 0.55 },
  };
}

/** A straight extrusion of a side profile across the car, centred on it: no chamfers. */
function slab(outline: Shape, width: number, curveSegments = 2): ExtrudeGeometry {
  const geometry = new ExtrudeGeometry(outline, { depth: width, bevelEnabled: false, curveSegments });
  geometry.translate(0, 0, -width / 2);
  geometry.rotateY(-Math.PI / 2);
  return geometry;
}

/**
 * A parked car of a kind: its outline as the drivable one's — the same shell,
 * glass and roof profiles — but with none of its detail. No chamfers, arches
 * of a few facets, wheels that are a tyre and a rim, lamps that are flat
 * panels; no mirrors, handles, plates or pillars. Some 280 triangles to the
 * drivable model's 2,000: a street holds dozens, a city tile hundreds, and on
 * a phone the parked cars were most of the frame. The body is `paint`, for
 * an instance colour to tint.
 */
export function createParkedCar(kind: CarOnlyKind): { root: Group; paint: Material } {
  const spec = CARS[kind];
  const root = new Group();
  const paint = faceted(0xffffff, 0.32, 0.25);
  const half = spec.bodyWidth / 2;
  const wr = spec.wheelRadius;
  const axle = spec.wheelbase / 2;
  const arch = wr + 0.09;

  const shell = new Shape();
  shell.moveTo(spec.shell[0][0], spec.shell[0][1]);
  for (const [z, y] of spec.shell.slice(1)) shell.lineTo(z, y);
  shell.lineTo(axle + arch, spec.sill);
  shell.absarc(axle, wr, arch, 0, Math.PI, false);
  shell.lineTo(-axle + arch, spec.sill - 0.03);
  shell.absarc(-axle, wr, arch, 0, Math.PI, false);
  shell.closePath();
  const shellGeometry = slab(shell, spec.bodyWidth);
  root.add(part(shellGeometry, paint));
  const cabinWidth = spec.bodyWidth - 0.3;
  root.add(part(slab(profile(spec.cabin), cabinWidth), glass));
  root.add(part(slab(profile(spec.roof), spec.bodyWidth - 0.24), paint));
  const c2 = spec.cabin[2], c3 = spec.cabin[3];
  if (spec.cargoFrom !== undefined) {
    const length = c3[0] - spec.cargoFrom;
    root.add(part(new BoxGeometry(cabinWidth + 0.03, c2[1] - spec.belt - 0.04, length), paint, 0, (c2[1] + spec.belt) / 2, spec.cargoFrom + length / 2));
  }
  if (spec.bedFrom !== undefined) {
    // The bed's sides and tailgate, on the body's top: hollow, as an open bed is.
    const top = spec.shell[5][1];
    const length = spec.shell[6][0] - spec.bedFrom - 0.06;
    for (const side of [-1, 1]) root.add(part(new BoxGeometry(0.07, 0.42, length), paint, side * (half - 0.04), top + 0.21, spec.bedFrom + length / 2));
    root.add(part(new BoxGeometry(spec.bodyWidth - 0.08, 0.42, 0.07), paint, 0, top + 0.21, spec.bedFrom + length));
  }

  // Bumpers, and lamps: a panel each, a finger's width proud of the nose and the tail.
  const fl = spec.frontLamp, rl = spec.rearLamp, bumper = spec.bumper;
  root.add(part(new BoxGeometry(spec.bodyWidth + 0.02, 0.17, 0.16), trim, 0, bumper, surfaceZ(shellGeometry, bumper, -1) + 0.02));
  root.add(part(new BoxGeometry(spec.bodyWidth + 0.02, 0.18, 0.16), trim, 0, bumper + 0.01, surfaceZ(shellGeometry, bumper, 1) - 0.02));
  const front = surfaceZ(shellGeometry, fl, -1) - 0.01, back = surfaceZ(shellGeometry, rl, 1) + 0.01;
  for (const side of [-1, 1] as const) {
    root.add(part(new PlaneGeometry(0.36, 0.12).rotateY(Math.PI), headlamp, side * (half - 0.33), fl, front));
    root.add(part(new PlaneGeometry(0.34, 0.15), taillamp, side * (half - 0.27), rl, back));
  }

  // Wheels: a tyre, its outer wall, and a rim on that.
  const x = half - 0.115, w = VEHICLE.tyreWidth;
  for (const side of [-1, 1] as const) {
    for (const z of [-axle, axle]) {
      root.add(part(new CylinderGeometry(wr, wr, w, 8, 1, true).rotateZ(Math.PI / 2), tyre, side * x, wr, z));
      root.add(part(new CircleGeometry(wr, 8).rotateY(side * Math.PI / 2), tyre, side * (x + w / 2), wr, z));
      root.add(part(new CircleGeometry(wr * 0.62, 8).rotateY(side * Math.PI / 2), rim, side * (x + w / 2 + 0.005), wr, z));
    }
  }
  return { root, paint };
}

/**
 * The car's ride: where the chassis sits, following the ground through its
 * suspension rather than welded to it. The ground here is flat triangles with
 * creases between them; a chassis placed exactly on it every frame jolts at
 * every crease, a sprung one rides over them.
 */
export interface Ride {
  y: number;
  vy: number;
  pitch: number;
  vPitch: number;
  roll: number;
  vRoll: number;
  wheels: [number, number, number, number];
  started: boolean;
  /** Seconds since the last landing: the springs are softer while it settles. */
  sinceLanding: number;
  /** How flattened the tyres are, 0..1: a landing's squash, springing back. */
  squash: number;
}

export function createRide(): Ride {
  return { y: 0, vy: 0, pitch: 0, vPitch: 0, roll: 0, vRoll: 0, wheels: [0, 0, 0, 0], started: false, sinceLanding: 99, squash: 0 };
}

/**
 * Step the ride toward the ground: height, pitch and roll on critically
 * damped springs — stiff on the road, softer on grass — and each wheel's
 * travel eased toward the ground under it. The chassis may float a little
 * over a dip or press into a rise, never visibly into the ground.
 */
export function updateRide(
  ride: Ride,
  target: { y: number; forwardSlope: number; lateralSlope: number; wheelGround: [number, number, number, number] },
  offRoad: boolean,
  dt: number,
  /** Off the ground: the chassis goes where the car is, wheels hanging. */
  airborne = false,
  /** Vertical speed at a landing this frame (negative), for the springs to take. */
  landing = 0,
): Ride {
  const pitch = Math.atan(target.forwardSlope), roll = Math.atan(target.lateralSlope);
  if (!ride.started || (!airborne && Math.abs(target.y - ride.y) > 5)) {
    Object.assign(ride, { y: target.y, vy: 0, pitch, vPitch: 0, roll, vRoll: 0, started: true });
  }
  if (airborne) {
    // Flying: exactly where the car is; its nose keeps the angle it left at,
    // dipping slowly as it falls; the wheels drop to the ends of their travel.
    ride.y = target.y;
    ride.vy = 0;
    ride.vPitch = 0;
    ride.pitch -= 0.08 * dt;
    for (let i = 0; i < 4; i++) ride.wheels[i] += (-0.1 - ride.wheels[i]) * (1 - Math.exp(-dt * 8));
    return ride;
  }
  ride.sinceLanding += dt;
  ride.squash *= Math.exp(-dt * 9);
  if (landing < -0.5) {
    // The springs take the landing: the body carries on down into them —
    // the harder, the deeper — the nose dips, the tyres flatten, and the
    // whole car wobbles a little as it comes back up.
    const hard = Math.min(1, -landing / 8);
    ride.vy = Math.max(-6.5, landing * 0.95);
    ride.vPitch += Math.max(-1.4, landing * 0.11);
    ride.vRoll += (Math.random() - 0.5) * 0.6 * hard;
    ride.squash = Math.min(0.28, 0.08 + 0.2 * hard);
    ride.sinceLanding = 0;
  }
  const w = offRoad ? 9 : 16;
  // A car's suspension is damped a little under critical. Just after a
  // landing it is let run softer, so the body rides back up past its rest
  // and bobs twice or three times before it settles — a car on its springs,
  // not a ball — then the usual damping takes over again.
  const settle = Math.min(1, ride.sinceLanding / 1.1);
  const zeta = 0.32 + (0.62 - 0.32) * settle;
  const steps = Math.max(1, Math.ceil(dt / (1 / 120)));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    ride.vy += (w * w * (target.y - ride.y) - 2 * zeta * w * ride.vy) * h;
    ride.y += ride.vy * h;
    ride.vPitch += (w * w * (pitch - ride.pitch) - 2 * zeta * w * ride.vPitch) * h;
    ride.pitch += ride.vPitch * h;
    ride.vRoll += (w * w * (roll - ride.roll) - 2 * zeta * w * ride.vRoll) * h;
    ride.roll += ride.vRoll * h;
  }
  // The body lags the ground by no more than the springs travel: over a kerb
  // or onto a road it eases up after the wheels, and landing it sinks into
  // them, then rises back — and never floats far off.
  const floor = target.y - 0.32;
  if (ride.y < floor) { ride.y = floor; ride.vy = Math.max(0, ride.vy); }
  ride.y = Math.min(target.y + 0.3, ride.y);
  // The wheels stay on the ground wherever the body is: their travel is the
  // ground under each one, plus however far the body lags above or below it.
  const lag = target.y - ride.y;
  const k = 1 - Math.exp(-dt * (offRoad ? 14 : 22));
  for (let i = 0; i < 4; i++) {
    const want = Math.max(-0.34, Math.min(0.34, target.wheelGround[i] + lag));
    ride.wheels[i] += (want - ride.wheels[i]) * k;
  }
  return ride;
}

/** What the model needs to know each frame to move like a car. */
export interface VehicleMotion {
  speed: number;
  steerAngle: number;
  /** Along the heading, m/s²: squat and dive. */
  accel: number;
  /** Sideways, m/s², positive to the right: the lean. */
  lateralAccel: number;
  braking: boolean;
  /** 0 day … 1 night. */
  night: number;
  /** Per wheel, how far the ground under it is above (+) or below the chassis plane, m. */
  wheelGround?: [number, number, number, number];
  /** On grass: the body shakes a little. */
  offRoad?: boolean;
  /** How flattened the tyres are, 0..1, from a landing. */
  squash?: number;
  /** Main beam: the lights at full strength, reaching far down the road. */
  highBeam?: boolean;
}

/** The sprung body's state between frames. */
const springs = new WeakMap<VehicleModel, { pitch: number; pitchV: number; roll: number; rollV: number; t: number }>();

/**
 * Move the car's parts for a frame: spin and steer the wheels, let the body
 * ride its springs, light the lamps.
 */
export function animateVehicle(model: VehicleModel, motion: VehicleMotion, dt: number): void {
  const r = model.wheelRadius;
  model.wheels.forEach((wheel, i) => {
    // Rolling forward (toward -Z) turns the top of the wheel forward.
    wheel.spin.rotation.x -= (motion.speed / r) * dt;
    wheel.pivot.rotation.y = (wheel.front ? -motion.steerAngle : 0) + (wheel.toe ?? 0);
    const travel = Math.max(-0.34, Math.min(0.34, motion.wheelGround?.[i] ?? 0));
    // A flattened tyre is lower at its hub and wider at its footprint.
    const squash = motion.squash ?? 0;
    wheel.pivot.position.y = r * (1 - squash) + travel + (wheel.drop ?? 0);
    wheel.pivot.scale.set(1 + squash * 0.35, 1 - squash, 1 + squash * 0.25);
  });
  // The trailer's wheels roll with it; it is steered by its kingpin, not by them.
  const trailer = model.trailer;
  if (trailer !== undefined) for (const wheel of trailer.wheels) wheel.spin.rotation.x -= (motion.speed / trailer.wheelRadius) * dt;

  // The body on its springs: squat under power, dive under braking, lean out
  // of a corner, with a little overshoot, as a car does.
  const s = springs.get(model) ?? { pitch: 0, pitchV: 0, roll: 0, rollV: 0, t: 0 };
  s.t += dt;
  const pitchTarget = Math.max(-0.05, Math.min(0.05, motion.accel * 0.006));
  const rollTarget = Math.max(-0.07, Math.min(0.07, motion.lateralAccel * 0.008));
  const k = 70, c = 9;
  s.pitchV += ((pitchTarget - s.pitch) * k - s.pitchV * c) * dt;
  s.pitch += s.pitchV * dt;
  s.rollV += ((rollTarget - s.roll) * k - s.rollV * c) * dt;
  s.roll += s.rollV * dt;
  springs.set(model, s);
  // Off the road the body rocks gently on its springs over the unevenness: a
  // few slow, overlapping sways, stronger with speed — the way a car moves
  // across a field, rather than a buzz.
  const rough = motion.offRoad === true ? Math.min(1, Math.abs(motion.speed) / 12) : 0;
  const t = s.t;
  const rockPitch = rough * 0.012 * (Math.sin(t * 5.3) * 0.6 + Math.sin(t * 8.9 + 1.3) * 0.4);
  const rockRoll = rough * 0.014 * (Math.sin(t * 4.1 + 0.7) * 0.6 + Math.sin(t * 7.7 + 2.1) * 0.4);
  const bob = rough * 0.018 * (Math.sin(t * 6.7) * 0.5 + Math.sin(t * 10.3 + 0.4) * 0.5);
  // Nose-up is positive X in this frame; the body leans away from the turn.
  model.body.rotation.set(s.pitch + rockPitch, 0, s.roll + rockRoll);
  model.body.position.y = bob;

  // Lamps: a glow by day, lit at night; brakes brighter still.
  headlamp.emissiveIntensity = 0.35 + 2.4 * motion.night;
  taillamp.emissiveIntensity = motion.braking ? 2.6 : 0.4 + 1.2 * motion.night;
  // Dipped, the beams light the road ahead and the verges and come down
  // short. Main beam reaches four times as far, aimed higher and straighter.
  const high = motion.highBeam === true;
  for (const beam of model.headlights) {
    const side = Math.sign(beam.position.x) || 1;
    beam.intensity = (high ? 170 : 28) * motion.night;
    beam.distance = high ? 220 : 60;
    // Fading slower with distance, so the far road is lit, not just nearer.
    beam.decay = high ? 1.1 : 1.4;
    beam.angle = high ? 0.3 : 0.42;
    beam.target.position.set(beam.position.x + side * (high ? 0.4 : 1.5), high ? 1.6 : 0, high ? -80 : -22);
  }
  if (high) headlamp.emissiveIntensity = 0.35 + 4 * motion.night;
}
